import { makeStyles } from 'tss-react/mui';

import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Chip,
  CustomAutocomplete,
  CustomSelect,
  MUIDataTable,
  MUIDataTableColumnDef,
  transformCustomDropdownData,
  Typography,
} from 'web-library';
import { DataArchivalTemplate, DataLevel } from '../../../../api-refactored/data-archival';
import { CompleteDataArchivalContent } from '../../../../api-refactored/data-archival/useGetDataArchivalDetail';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(1),
  },
  groupDescription: {
    fontWeight: 700,
    color: theme.color.text.textTertiary,
    marginBottom: theme.spacing(2),
  },
  checkbox: {
    padding: '9px',
    marginRight: theme.spacing(1),
    margin: 0,
  },
}));

interface TableColumnsProps {
  t: TFunction;
  classes: Record<string,string>;
  template: DataArchivalTemplate[] | CompleteDataArchivalContent[];
  editable: boolean;
  setSelected: React.Dispatch<React.SetStateAction<DataLevel[][]>>;
}

const defaultYesOption = {
  uuid: 'Yes',
  displayName: 'Yes',
};
const defaultNoOption = {
  uuid: 'No',
  displayName: 'No',
};

const tableColumns = ({
  t,
  classes,
  template,
  editable,
  setSelected,
}: TableColumnsProps): MUIDataTableColumnDef[] => [
    {
      name: 'dataGroups',
      label: t('admin->>DataArchival.ColumnDataGroup'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { width: '50%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const rowData = template[dataIndex];

          return (
            <Box key={rowData.uuid}>
              {rowData.dataGroup.map((group) => (
                <Box key={group.name}>
                  {group.name}
                  <br />
                  <Typography className={classes.groupDescription} variant="subtitle2">
                    {group.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        },
      },
    },

    {
      name: 'dataArchivalLevel',
      label: t('admin->>DataArchival.ColumnDataArchivalLevel'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({
          style: {
            verticalAlign: 'middle',
          },
        }),
        customBodyRenderLite: (dataIndex: number) => {
          const rowData = template[dataIndex];

          // single select
          if (!rowData.withDataLevel) {
            const singleFunctionCode = transformCustomDropdownData(
              [defaultYesOption, defaultNoOption],
              (i) => ({
                key: i.uuid,
                children: i.displayName,

                value: i.displayName,
              })
            );
            // if editable, create mode, no default value
            let singleDefaultValue = defaultNoOption.uuid;
            // not editable, view mode, need default value
            if (!editable) {
              singleDefaultValue = (rowData as CompleteDataArchivalContent).selected
                ? defaultYesOption.uuid
                : defaultNoOption.uuid;
            }

            return (
              <CustomSelect
                variant="filled"
                displayEmpty
                disabled={!editable}
                defaultValue={singleDefaultValue}
                placeholder={t('admin->>DataArchival.ArchivalLevelPlaceholder')}
                menuItems={singleFunctionCode}
                onChange={(event) => {
                  const selectedValue = event.target.value as string;
                  setSelected((val) => {
                    const gridClone = [...val];
                    // no selection or select 'No' both consider no, will return empty array to ease form validation(at least one selection)
                    gridClone[dataIndex] =
                      selectedValue === defaultYesOption.uuid ? [defaultYesOption] : [];
                    return gridClone;
                  });
                }}
              />
            );
          }

          // if editable, create mode, no default value
          const multiDefaultValue: DataLevel[] = [];
          const multiFunctionCode = rowData.dataLevel;
          // not editable, view mode, need default value
          if (!editable) {
            (rowData as CompleteDataArchivalContent).selectedDataLevel?.forEach((item) => {
              const reverseItem = multiFunctionCode.find((i) => i.uuid === item);
              if (reverseItem) {
                multiDefaultValue.push(reverseItem);
              }
            });
          }

          // multi select
          return (
            <CustomAutocomplete<DataLevel, true, false, undefined>
              multiple
              id="select-data-archival-level"
              disableCloseOnSelect
              placeholder={t('admin->>DataArchival.ArchivalLevelPlaceholder')}
              disabled={!editable}
              defaultValue={multiDefaultValue}
              options={multiFunctionCode}
              getOptionLabel={(option) => option.displayName}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              renderOption={(_, option, { selected }, __) => (
                <>
                  <Checkbox
                    className={classes.checkbox}
                    checked={selected}
                    id={`autocomplete-option-${option.uuid}`}
                  />
                  {option.displayName}
                </>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, tagIndex) => (
                  <Chip
                    {...getTagProps({ index: tagIndex })}
                    key={`chip-${option.uuid}`}
                    id={`chip-data-archival-level-${option.uuid}`}
                    label={option.displayName}
                  />
                ))
              }
              onChange={(event, value) =>
                setSelected((val) => {
                  const gridClone = [...val];
                  gridClone[dataIndex] = value as DataLevel[];
                  return gridClone;
                })
              }
            />
          );
        },
      },
    },
  ];

interface FormTableDataGroupProps {
  template: DataArchivalTemplate[] | CompleteDataArchivalContent[];
  editable?: boolean;
  setSelected?: React.Dispatch<React.SetStateAction<DataLevel[][]>>;
}

const FormTableDataGroup = ({
  template,
  editable = true,
  setSelected = () => {
    // default empty set function
  },
}: FormTableDataGroupProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  // ui
  return (
    <MUIDataTable
      columns={tableColumns({
        t,
        classes,
        template: template || [],
        editable,
        setSelected,
      })}
      data={template || []}
    />
  );
};

export default FormTableDataGroup;
